import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";

// Sellers Stripe cannot pay: what each is owed for delivered orders. An admin
// checks their details, sends the money by mobile money or bank, then
// records it here with the transfer's reference.

const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const accountLines = (account) => {
  if (!account || account.method === "stripe") return [];
  return account.method === "mobile_money"
    ? [`Mobile money: ${account.provider}`, `Number: ${account.phone}`, `Name: ${account.accountName}`]
    : [
        `Bank: ${account.bankName}`,
        `Account: ${account.accountNumber}`,
        ...(account.bankCode ? [`SWIFT / branch: ${account.bankCode}`] : []),
        `Name: ${account.accountName}`,
      ];
};

const PayDialog = ({ seller, onClose, onPaid }) => {
  const [reference, setReference] = useState("");
  const [fee, setFee] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const feeCents = Math.round(Number(fee || 0) * 100);

  const pay = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await authFetch(`/api/seller-payouts/${seller.sellerId}/pay`, {
        method: "POST",
        body: JSON.stringify({ reference, note, feeCents, lineIds: seller.lines.map((l) => l.id) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not record the payout");
      onPaid();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mark as paid: {seller.name}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          Earned on {seller.lines.length} order line(s): <b>{money(seller.owedCents)}</b>
          {feeCents > 0 && (
            <>
              {" "}
              · sent to the seller: <b>{money(seller.owedCents - feeCents)}</b>
            </>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send the money first, then record it. {accountLines(seller.account).join(" · ")}
        </Typography>
        <TextField
          fullWidth
          required
          margin="dense"
          label="Transfer reference (from the receipt)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
        <TextField
          fullWidth
          margin="dense"
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
          label="Transfer fee taken from the seller, in USD (optional)"
          value={fee}
          onChange={(e) => setFee(e.target.value)}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Note (optional), for example the amount in local currency"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={pay} disabled={saving || reference.trim().length < 3}>
          {saving ? "Saving…" : "Record payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SellerCard = ({ seller, onPay, onVerified }) => {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const account = seller.account;
  const manual = account && account.method !== "stripe";
  const verified = Boolean(account?.verifiedAt);

  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await authFetch(`/api/seller-payouts/${seller.sellerId}/verify`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not save");
      onVerified();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {seller.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[seller.email, seller.phone, seller.country].filter(Boolean).join(" · ")}
          </Typography>
          {manual ? (
            accountLines(account).map((line) => (
              <Typography variant="body2" key={line}>
                {line}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
              No payment details yet. Ask the seller to choose how they want to be paid.
            </Typography>
          )}
        </Box>
        <Box textAlign="right">
          <Typography variant="h4" fontWeight={700}>
            {money(seller.owedCents)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            to pay now · {money(seller.waitingCents)} waiting for delivery
          </Typography>
          {manual && (
            <Chip
              size="small"
              sx={{ mt: 0.5 }}
              color={verified ? "success" : "warning"}
              label={verified ? "Details checked" : "Details not checked"}
            />
          )}
        </Box>
      </Box>
      <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        {manual && !verified && (
          <Button variant="outlined" onClick={verify} disabled={busy}>
            Name matches the seller: details checked
          </Button>
        )}
        <Button
          variant="contained"
          color="success"
          disabled={!manual || !verified || seller.owedCents <= 0}
          onClick={() => onPay(seller)}
        >
          Mark as paid
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

const SellerPayouts = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(null);
  const [done, setDone] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch("/api/seller-payouts");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not load seller payouts");
      setSellers(data.sellers || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = sellers.reduce((sum, s) => sum + s.owedCents, 0);

  return (
    <Box m="20px" height="75vh" overflow="auto" paddingRight="12px">
      <Header
        title="SELLER PAYOUTS"
        subtitle={`Sellers paid by mobile money or bank. To pay now: ${money(total)}`}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {done && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDone("")}>
          {done}
        </Alert>
      )}
      {!loading && !error && sellers.length === 0 && <Typography>Nothing to pay right now.</Typography>}
      {sellers.map((seller) => (
        <SellerCard key={seller.sellerId} seller={seller} onPay={setPaying} onVerified={load} />
      ))}
      {paying && (
        <PayDialog
          seller={paying}
          onClose={() => setPaying(null)}
          onPaid={() => {
            setDone(`Payment to ${paying.name} recorded. The seller was emailed.`);
            setPaying(null);
            load();
          }}
        />
      )}
    </Box>
  );
};

export default SellerPayouts;
